/**
 * @module context/ChartSettingsContext
 * @description ChartSettingsContext — useReducer + Provider + Selector Hooks
 *
 * 架构（与 V2 QueryContext.tsx / ResultContext.tsx 风格一致）：
 * - createContext + useReducer + Provider + selector hooks
 * - dispatch 引用稳定（useReducer 保证），不触发不必要的 Provider 重渲染
 * - useChartSettingsSelector 精确订阅（基于内嵌 context-selector v1.4.1），仅 selector 返回值变化时重渲染
 *
 * 数据流：
 *   组件 → dispatch(UPDATE_DRAFT, { yAxisName: '新值' })
 *   → Reducer 更新 draft + hasDraft
 *   → Provider 通知订阅者（按 selector 结果 Object.is 比较）
 *   → 组件通过 useChartSettingsSelector 读取新值
 *
 * @see 状态管理方案 §三 — Context 架构
 */

import React, { useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import {
  registerChartSettingsDispatch,
  registerChartSettingsState,
  resetChartSettingsBridgeSession,
  resetChartSettingsSession,
  unregisterChartSettingsDispatch,
} from '../utils/chartSettingsBridge';
import {
  defineManagedContext,
  useManagedSelector,
} from '@/pages/datalab/utils/managedContext';
import {
  CSActionType,
  INITIAL_STATE,
  type IChartSettingsState,
  type TCSAction,
} from '../types';
import { mergeConfigLayer } from '../utils/mergeConfigPatch';
import type { IColumnPercentageItem } from '../linkage/qbFields/qbLinkage/handlers/columnSort/untils/columnPercentage';
import type { IDimensionPercentageConfig } from '../types/settings/dimensionPercentage';
import { syncColumnSortVirtualColumns } from '../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/syncColumnSortVirtualColumns';
import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import {
  getBaseColumnSortList,
  hasUnsavedPanelColumnSortOrder,
  isColumnSortOrderEqual,
} from '../linkage/qbFields/qbLinkage/handlers/columnSort/untils/keys';

// ── Context Value 类型 ──

interface ICSContextValue {
  state: IChartSettingsState;
  /** dispatch 引用稳定（useReducer 保证） */
  dispatch: React.Dispatch<TCSAction>;
}

const CHART_SETTINGS_OUTSIDE_PROVIDER =
  '[ChartSettings] useChartSettings* called outside ChartSettingsProvider';

// ── Context 创建（须使用 context-selector 的 createContext + Provider）──

/** ChartSettingsContext — 初始值为 null，Provider 缺失时 throw */
const ChartSettingsContext = defineManagedContext<ICSContextValue | null>(null);

function assertChartSettingsContext(
  ctx: ICSContextValue | null,
): asserts ctx is ICSContextValue {
  if (!ctx) {
    throw new Error(CHART_SETTINGS_OUTSIDE_PROVIDER);
  }
}

// ── Reducer ──

/**
 * chartSettingsReducer — 与 V2 queryReducer / resultReducer 风格一致
 *
 * 状态更新遵循不可变原则，所有 case 返回新对象
 */
export function chartSettingsReducer(
  state: IChartSettingsState,
  action: TCSAction,
): IChartSettingsState {
  switch (action.type) {
    // ── 面板控制 ──
    case CSActionType.SET_MODAL:
      return { ...state, chartsModal: action.payload };

    // ── Confirm 重算触发版本号（Search useUpdateEffect 监听递增）──
    case CSActionType.BUMP_RECALC_TRIGGER:
      return {
        ...state,
        recalcTriggerVersion: state.recalcTriggerVersion + 1,
      };

    case CSActionType.SET_ACTIVE_TAB:
      return { ...state, activeTabs: action.payload };

    // ── BASE 写入：浅合并，仅更新传入字段 ──
    case CSActionType.SET_BASE:
      return {
        ...state,
        base: { ...state.base, ...action.payload },
      };

    // ── BASE 整表替换（进页初始化 / QB 重置，不按图表类型残留旧 key）──
    case CSActionType.SET_BASE_REPLACE:
      return {
        ...state,
        base: { ...action.payload },
      };

    // ── Draft 更新：undefined 表示从 draft 移除该字段（非空数组占位）──
    case CSActionType.UPDATE_DRAFT: {
      const draft = { ...state.draft };
      const hasDraft = { ...state.hasDraft };
      const payload = { ...action.payload };
      let nextBase = state.base;

      // 列占比 / 维度占比 / 面板拖拽 column_sort：同步 % 与 dim_pct 虚拟列
      if (
        'column_sort' in payload ||
        'column_percentage' in payload ||
        'dimension_percentage' in payload
      ) {
        const columnPercentageForSync: IColumnPercentageItem[] =
          'column_percentage' in payload
            ? (payload.column_percentage as IColumnPercentageItem[]) || []
            : Array.isArray(draft.column_percentage)
              ? (draft.column_percentage as IColumnPercentageItem[])
              : Array.isArray(state.base.column_percentage)
                ? (state.base.column_percentage as IColumnPercentageItem[])
                : [];

        const dimensionPercentageForSync: IDimensionPercentageConfig | undefined =
          'dimension_percentage' in payload
            ? (payload.dimension_percentage as IDimensionPercentageConfig)
            : 'dimension_percentage' in draft
              ? (draft.dimension_percentage as IDimensionPercentageConfig)
              : (state.base.dimension_percentage as IDimensionPercentageConfig);

        const existingSort: IColumnSortItem[] | undefined =
          'column_sort' in payload && Array.isArray(payload.column_sort)
            ? (payload.column_sort as IColumnSortItem[])
            : 'column_sort' in draft &&
                Array.isArray(draft.column_sort) &&
                draft.column_sort.length > 0
              ? (draft.column_sort as IColumnSortItem[])
              : Array.isArray(state.base.column_sort) &&
                  state.base.column_sort.length > 0
                ? (state.base.column_sort as IColumnSortItem[])
                : undefined;

        if (existingSort) {
          const syncedColumnSort = syncColumnSortVirtualColumns(
            existingSort,
            columnPercentageForSync,
            dimensionPercentageForSync,
          );
          payload.column_sort = syncedColumnSort;

          // 已持久化 base.column_sort 时，列/维度占比触发的虚拟列同步须同时写 base，
          // 否则未 Confirm 前计算/上传仍读旧 base（缺 dim_pct / % 列）
          const isPercentageDerivedColumnSortSync =
            'column_percentage' in payload ||
            'dimension_percentage' in payload;
          const baseHasColumnSort =
            Array.isArray(state.base.column_sort) &&
            state.base.column_sort.length > 0;
          if (
            isPercentageDerivedColumnSortSync &&
            baseHasColumnSort &&
            syncedColumnSort.length > 0
          ) {
            nextBase = { ...state.base, column_sort: syncedColumnSort };
          }
        }
      }

      for (const [key, value] of Object.entries(payload)) {
        hasDraft[key] = true;
        if (value === undefined) {
          delete draft[key];
        } else {
          draft[key] = value;
        }
      }
      return { ...state, base: nextBase, draft, hasDraft };
    }

    // ── Draft → Base 合并；hasDraft 中已移除的 key 从 base 同步删除 ──
    case CSActionType.COMMIT_DRAFT: {
      const base = { ...state.base, ...state.draft };
      for (const key of Object.keys(state.hasDraft)) {
        if (!(key in state.draft)) {
          delete base[key];
        }
      }
      return {
        ...state,
        base,
        draft: {},
        hasDraft: {},
      };
    }

    // ── 丢弃 Draft（Cancel） ──
    case CSActionType.RESET_DRAFT:
      return { ...state, draft: {}, hasDraft: {} };

    case CSActionType.MERGE_QB_PATCH: {
      const { base: basePatch, draft: draftPatch } = action.payload;
      let effectiveDraftPatch = draftPatch;

      // 仅拦截 stale 误触：把未保存面板列序拉回 base；QB 真实重排 patch ≠ base 时仍合并
      if (
        draftPatch?.column_sort &&
        hasUnsavedPanelColumnSortOrder({
          base: state.base,
          draft: state.draft,
        })
      ) {
        const patched = draftPatch.column_sort as IColumnSortItem[];
        if (
          isColumnSortOrderEqual(patched, getBaseColumnSortList({ base: state.base }))
        ) {
          const { column_sort: _ignored, ...rest } = draftPatch;
          effectiveDraftPatch =
            Object.keys(rest).length > 0 ? rest : undefined;
        }
      }

      return {
        ...state,
        base: basePatch
          ? mergeConfigLayer(state.base, basePatch)
          : state.base,
        draft: effectiveDraftPatch
          ? mergeConfigLayer(state.draft, effectiveDraftPatch)
          : state.draft,
      };
    }

    // ── 批量更新 ──
    case CSActionType.PATCH:
      return { ...state, ...action.payload };

    // ── 恢复初始状态（整页 reset：离开页 / 菜单新建；与 DVA resetState 对齐）──
    case CSActionType.RESET:
      return { ...INITIAL_STATE };

    default:
      return state;
  }
}

// ── Provider ──

/**
 * 页面级 Provider — 仅 ChartSettings Context。
 * 各 setting 组件通过 selector 分别订阅 DVA Query/Result 与本 Context。
 */
export const ChartSettingsProvider: React.FC<{ children: React.ReactNode; }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(chartSettingsReducer, INITIAL_STATE);
  // ref 供 bridge 在 Provider 外读取最新 state；避免 useLayoutEffect 依赖 state 导致每次 dispatch 重置 bridge 签名
  const stateRef = useRef(state);
  stateRef.current = state;
  const value = useMemo<ICSContextValue>(() => ({ state, dispatch }), [state]);

  useLayoutEffect(() => {
    resetChartSettingsBridgeSession();
    registerChartSettingsDispatch(dispatch);
    registerChartSettingsState(() => stateRef.current);
    return () => {
      // 离开页：显式 RESET，与 index 侧 DVA resetState 配套，防止 Search 仍读到旧 base
      resetChartSettingsSession();
      unregisterChartSettingsDispatch();
    };
  }, [dispatch]);

  return (
    <ChartSettingsContext.Provider value={value}>
      {children}
    </ChartSettingsContext.Provider>
  );
};

// ── Selector Hooks（与 V2 useQueryState / useResultState 风格一致）──

/**
 * 读取完整 state（粗粒度，仅在需要全量状态时使用）
 * 注意：state 引用在任意 dispatch 后都会变化，大多数场景应用 useChartSettingsSelector
 */
export function useChartSettingsState(): IChartSettingsState {
  return useManagedSelector(ChartSettingsContext, ctx => {
    assertChartSettingsContext(ctx);
    return ctx.state;
  });
}

/**
 * 获取 dispatch（引用稳定，useReducer 保证）
 * 组件可安全地在 useEffect 依赖中省略 dispatch
 */
export function useChartSettingsDispatch(): React.Dispatch<TCSAction> {
  return useManagedSelector(ChartSettingsContext, ctx => {
    assertChartSettingsContext(ctx);
    return ctx.dispatch;
  });
}

/**
 * 选择器 — 精确订阅，仅 selector 返回值 Object.is 变化时触发重渲染
 *
 * @param selector - 从 IChartSettingsState 中选择需要的值
 *
 * @example
 * const yAxisName = useChartSettingsSelector(s => s.base.yAxisName);
 * const hasUnsaved = useChartSettingsSelector(s => Object.keys(s.hasDraft).length > 0);
 *
 * 注意：勿在 selector 内使用 `?? []` / `?? {}`，QB 列兜底请用 `utils/constants` 的 `EMPTY_COLUMN_INFO_LIST`
 */
export function useChartSettingsSelector<T>(
  selector: (s: IChartSettingsState) => T,
): T {
  return useManagedSelector(ChartSettingsContext, ctx => {
    assertChartSettingsContext(ctx);
    return selector(ctx.state);
  });
}
